import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { RoundedSubHeader } from "../../components/Headings/Headings";
import ConsultHero from './ConsultHero';
import { Footer } from '../../container';
import data_eng from '../../constants/data_eng';
import data from '../../constants/data';
import WorkWithLogos from '../../components/IconScroll/WorkWithLogos';
import ConsultTestimonials from '../../container/Testimonials/ConsultTestimonials';
import ReactMarkdown from 'react-markdown';
import { linkifyText } from '../News/InternalHyperLinks';
import { consultSchemaSv } from '../../constants/localisations/schema_markups/schema_markups_sv';
import { consultSchemaEng } from '../../constants/localisations/schema_markups/schema_markups_eng';
import { useLanguage } from '../../constants/localisations/language_context';
import { LANG_ENG, LANG_SV, translatedInternalLink, CONSULT } from '../../constants/strings';
import SeoMeta from '../../components/SeoMeta';
import { Nav } from 'react-bootstrap';
import { FaqList } from '../../container';

const PAGE_UPDATED_LABEL_SV = '15 augusti 2026';
const PAGE_UPDATED_LABEL_ENG = 'August 15, 2026';

const Consult = () => {
  const { currentLanguage } = useLanguage();
  const schemaMarkup = currentLanguage === LANG_ENG ? consultSchemaEng : consultSchemaSv;
  const currentData = currentLanguage === LANG_ENG ? data_eng.ServicesExtendedData : data.ServicesExtendedData;
  const consultFaqs = currentLanguage === LANG_ENG ? data_eng.ConsultFaqs : data.ConsultFaqs;

  // sv and en are always both live for this page (same component, two routes),
  // so the alternates are unconditional — unlike News.jsx where a twin may not exist.
  const hreflangSv = translatedInternalLink(CONSULT, LANG_SV);
  const hreflangEng = translatedInternalLink(CONSULT, LANG_ENG);

  const updatedLabel = currentLanguage === LANG_ENG ? PAGE_UPDATED_LABEL_ENG : PAGE_UPDATED_LABEL_SV;
  const updatedText = currentLanguage === LANG_ENG
    ? `Updated ${updatedLabel} by Johannes Olsson, AI Consultant at Techlära`
    : `Uppdaterad ${updatedLabel} av Johannes Olsson, AI-konsult på Techlära`;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: consultFaqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  const faqTitle = currentLanguage === LANG_ENG ? 'FAQ' : 'Vanliga frågor';
  const faqText = currentLanguage === LANG_ENG
    ? 'Common questions about AI consulting'
    : 'Vanliga frågor om AI-konsultering';

  useEffect(() => {
    document.documentElement.lang = currentLanguage === LANG_ENG ? 'en' : 'sv';
    document.title = currentLanguage === LANG_ENG
      ? "AI Consultant in Stockholm - We help your company implement AI | techlära.se"
      : "AI-konsult i Stockholm - Vi hjälper ert företag med AI | techlära.se";
  }, [currentLanguage]);

  const services = () => {
    return (
      <>
        {currentData.map(({ header, headerTagline, text, link, linkTitle }, index) => (
          <div className="service-container" key={index}>
            <RoundedSubHeader title={header} text={headerTagline} />
            <div >
              <ReactMarkdown>
                {linkifyText(text, { language: currentLanguage })}
              </ReactMarkdown>
              {link &&
                <div>
                  <br />
                  <a href={link}>{linkTitle}</a>
                </div>
              }
            </div>
            <br /><br />
          </div>
        ))}
      </>
    );
  }

  const handleButtonClick = () => {
    window.open("https://calendly.com/techlara/30min", "_blank");
  };

  return (
    <div className="consult-container">
      <Helmet>
        <html lang={currentLanguage === LANG_ENG ? 'en' : 'sv'} />
        <link rel="canonical" href={translatedInternalLink(CONSULT, currentLanguage)} />
        <link rel="alternate" hrefLang="sv" href={hreflangSv} />
        <link rel="alternate" hrefLang="en" href={hreflangEng} />
        <link rel="alternate" hrefLang="x-default" href={hreflangSv} />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        <meta
          name="description"
          content={currentLanguage === LANG_ENG
            ? "Professional AI consulting services for companies. We offer expertise in Claude and OpenAI implementation, strategy development and customized AI solutions."
            : "Professionella AI-konsulttjänster för företag. Vi erbjuder expertis inom Claude- och OpenAI-implementering och skräddarsydda AI-lösningar."}
        />
        <meta name="robots" content="index, follow" />
        <meta httpEquiv="X-Robots-Tag" content="index, follow" />
        <meta name="keywords" content={schemaMarkup.keywords.join(', ')} />
        <meta name="publisher" content={"Johannes Olsson - Techlära"} />
      </Helmet>
      <SeoMeta
        title={currentLanguage === LANG_ENG
          ? "AI Consulting - We help your company implementing AI - techlära.se"
          : "AI konsult - Vi hjälper ert företag med AI - techlära.se"}
        description={currentLanguage === LANG_ENG
          ? "Professional AI consulting services for companies. We offer expertise in Claude and OpenAI implementation, strategy development and customized AI solutions."
          : "Professionella AI-konsulttjänster för företag. Vi erbjuder expertis inom Claude- och OpenAI-implementering och skräddarsydda AI-lösningar."}
        url={translatedInternalLink(CONSULT, currentLanguage)}
      />
      <ConsultHero />
      <p className="consult-updated-meta" style={{ color: '#57534e', fontSize: '0.9rem' }}>
        {updatedText}
      </p>
      <br /><br />
      <ConsultTestimonials />
      <br />
      <WorkWithLogos />
      <br /><br /><br />
      Har du frågor? Kontakta oss på:
      <br />
      <Nav.Link className="link" href="mailto:hej@techlara.se?subject=Hej">hej@techlara.se</Nav.Link>

      <br /><br />
      {services()}
      <br />
      <FaqList faqs={consultFaqs} title={faqTitle} text={faqText} />
      <br />
      <button className="btn-green" onClick={handleButtonClick}>
        {currentLanguage === LANG_ENG ? "Book a meeting" : "Boka ett möte"}
      </button>

      <Footer />
    </div>
  );
};

export default Consult;